import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {
  GridRenderCellParams,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid';
import {isEqual, sortBy} from 'lodash';
import ArrDataGrid from '../../common/arr-data-grid/ArrDataGrid';
import ArrButton from '../../common/arr-button/ArrButton';
import {ReactComponent as NoDataIcon} from '../../../assets/images/no-data-found.svg';
import {annotationColumns} from './AnnotationListGridColumns';
import {
  useAnnotationListGrid,
  useDeleteAnnotation,
} from '../../../hooks/document/useDocument';
import {
  IAnnotation,
  IGetAnnotationRequestParams,
} from '../../../api/types/document.type';
import {PAGE_SIZE, ToastType} from '../../../shared/constants';
import {ISelectItem} from '../../../shared/types';

import UpsertAnnotationWrapper from '../components/upsert-comment/UpsertAnnotationWrapper';
import './../AnnotationList.scss';
import ArrModal from '../../common/arr-modal/ArrModal';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
interface IAnnotationListGridProps {
  selectedGroup: ISelectItem | null;
}

const AnnotationListGrid: React.FC<IAnnotationListGridProps> = ({
  selectedGroup,
}) => {
  const isOnlyPageChangeRef = useRef<boolean>(false);
  const [showUpsertAnnotationDialog, setShowUpsertAnnotationDialog] =
    useState<boolean>(false);
  const [annotationID, setAnnotationID] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState(ToastType.Success);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const apiRef = useGridApiRef();
  const [focusRowId, setFocusRowId] = useState<number | null>(null);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {field: 'layerName', sort: 'asc'},
  ]);

  const {mutate: deleteAnnotation, isPending} = useDeleteAnnotation();

  const handleDeleteAnnotation = (id: number) => {
    setAnnotationID(id);
    setShowDeleteModal(true);
  };

  const triggerToast = (
    toastType: ToastType | ((prevState: ToastType) => ToastType),
    toastMessage: string
  ) => {
    setShowToast(true);
    setShowDeleteModal(false);
    setToastType(toastType);
    setToastMessage(toastMessage);
  };

  const confirmDelete = () => {
    deleteAnnotation(
      {selectedAnnotationId: annotationID, selectedGroupId: selectedGroup?.id},
      {
        onSuccess: () => {
          triggerToast(
            ToastType.Success,
            t('annotationLayer.deleteAnnotation.success')
          );
          if (typeof getAnnotationListGrid === 'function')
            getAnnotationListGrid(prepareAnnotationRequest());
        },
        onError: () => {
          triggerToast(
            ToastType.Error,
            t('annotationLayer.deleteAnnotation.error')
          );
        },
      }
    );
  };

  const previousSelectedGroupRef = useRef<{
    selectedGroup: ISelectItem | null;
    sortModel: GridSortModel;
  }>({
    selectedGroup: null,
    sortModel: [{field: 'layerName', sort: 'asc'}],
  });
  const {
    mutate: getAnnotationListGrid,
    data: gridData,
    isPending: isAnnotationListFetching,
  } = useAnnotationListGrid();

  const isResetPageNumber = useCallback(() => {
    let resetPageNumber = false;

    if (
      previousSelectedGroupRef.current?.selectedGroup?.id !== selectedGroup?.id
    ) {
      previousSelectedGroupRef.current = {
        ...previousSelectedGroupRef.current,
        selectedGroup,
      };
      resetPageNumber = true;
    } else if (
      !isEqual(
        sortBy(previousSelectedGroupRef.current?.sortModel, ['field', 'sort']),
        sortBy(sortModel, ['field', 'sort'])
      )
    ) {
      previousSelectedGroupRef.current = {
        ...previousSelectedGroupRef.current,
        sortModel,
      };
      resetPageNumber = true;
    }

    return resetPageNumber;
  }, [selectedGroup, sortModel]);

  const prepareAnnotationRequest =
    useCallback((): IGetAnnotationRequestParams => {
      const getPageNumber = isOnlyPageChangeRef.current ? pageNumber : 1;

      const payload: IGetAnnotationRequestParams = {
        SortBy: sortModel[0]?.field || 'layerName',
        SortingOrder: sortModel[0]?.sort || 'asc',
        PageNumber: getPageNumber,
        PageSize: itemsPerPage,
      };

      if (selectedGroup?.id) {
        payload.SecurityGroupId = Number.parseInt(selectedGroup.id as string);
      }

      if (isResetPageNumber() && !isOnlyPageChangeRef.current) {
        setTimeout(() => setPageNumber(1), 100);
      }

      if (isOnlyPageChangeRef.current) {
        isOnlyPageChangeRef.current = false;
      }
      return payload;
    }, [
      isResetPageNumber,
      pageNumber,
      itemsPerPage,
      selectedGroup?.id,
      sortModel,
    ]);

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    setSortModel(model);
  }, []);

  const onPageChangeHandler = (value: number) => {
    isOnlyPageChangeRef.current = true;
    setPageNumber(value);
  };

  useEffect(() => {
    const requestPayload = prepareAnnotationRequest();
    getAnnotationListGrid(requestPayload);
  }, [
    itemsPerPage,
    pageNumber,
    sortModel,
    prepareAnnotationRequest,
    getAnnotationListGrid,
  ]);

  const handleEditAnnotation = useCallback((annotationId: number) => {
    setAnnotationID(annotationId);
    setShowUpsertAnnotationDialog(true);
    setFocusRowId(null);
  }, []);

  const updatedColumns = useMemo(
    () => [
      ...annotationColumns,
      {
        field: 'actions',
        headerName: 'Actions',
        sortable: false,
        filterable: false,
        flex: 1,
        headerClassName: 'typo-text label2_size more-option-header',
        renderHeader: () => (
          <Typography
            data-testid="annotation-actions-header"
            tabIndex={0}
            className="typo-text label2_size"
          >
            {t('annotationLayer.actions')}
          </Typography>
        ),
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box className="comments-grid-actions">
              <ArrButton
                dataTestId={`edit-annotation-field-${params.row.id}`}
                variant="secondary"
                id={`edit-btn-${params.row.id}`}
                size="xs"
                onClick={() => handleEditAnnotation(params.row.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.code === 'Space') {
                    e.stopPropagation();
                    handleEditAnnotation(params.row.id);
                  }
                }}
              >
                {t('common.edit')}
              </ArrButton>
              <ArrButton
                dataTestId={`delete-annotation-field-${params.id}`}
                variant="secondary"
                size="xs"
                onClick={() => handleDeleteAnnotation(params.row.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.code === 'Space') {
                    e.stopPropagation();
                    handleDeleteAnnotation(params.row.id);
                  }
                }}
              >
                {t('common.delete')}
              </ArrButton>
            </Box>
          );
        },
        cellClassName: 'action-cell',
      },
    ],
    [handleEditAnnotation, confirmDelete]
  );

  const annotationGridData = useMemo(
    () => gridData?.data ?? [],
    [gridData?.data]
  );
  const isNoData = useMemo(
    () => annotationGridData?.length <= 0,
    [annotationGridData?.length]
  );

  const gridDataTableRow = (item: IAnnotation) => ({
    id: item.annotationID,
    layerName: item.annotationName,
    description: item.annotationDescription,
  });
  const NoRowsOverlay = useCallback(
    () => (
      <Box className="no-project-data-found">
        <NoDataIcon />
        <Typography
          tabIndex={0}
          className="typo-text headline3_size no-list-text"
          data-testid={`annotation-list-grid-message-no-data`}
        >
          {t('savedSearch.savedSearchFilterGrid.grid.messageNoData')}
        </Typography>
      </Box>
    ),
    []
  );

  useEffect(() => {
    const api = apiRef.current;
    if (!api || focusRowId == null) return;

    const timeout = setTimeout(() => {
      const rowIndex = api.getRowIndexRelativeToVisibleRows(focusRowId);

      if (rowIndex !== -1) {
        api.setCellFocus(focusRowId, 'actions');
      }
    }, 0);

    return () => clearTimeout(timeout);
  }, [annotationGridData, focusRowId]);

  const onUpsertAnnotationSuccess = useCallback(() => {
    if (pageNumber === 1) {
      const requestPayload = prepareAnnotationRequest();
      getAnnotationListGrid(requestPayload);
    } else {
      setPageNumber(1);
    }
    setFocusRowId(annotationID);
  }, [
    getAnnotationListGrid,
    pageNumber,
    prepareAnnotationRequest,
    annotationID,
  ]);

  const handleAddAnnotation = useCallback(() => {
    if (selectedGroup?.id !== 0 && gridData && gridData.totalRecords > 0) {
      triggerToast(
        ToastType.Warning,
        t('annotationLayer.addEditAnnotation.warning')
      );
    } else {
      setAnnotationID(0);
      setShowUpsertAnnotationDialog(true);
    }
  }, [selectedGroup, gridData]);

  return (
    <>
      <Box display="flex" justifyContent="flex-end" pt={4} pb={2}>
        <ArrButton
          aria-label={t('annotationLayer.addAnnotation')}
          type="submit"
          variant="primary"
          title={t('annotationLayer.addAnnotation')}
          size="medium"
          onClick={handleAddAnnotation}
          dataTestId="annotation-list-grid-add-annotation-button"
        >
          {t('annotationLayer.addAnnotation')}
        </ArrButton>
      </Box>
      <ArrDataGrid
        apiRef={apiRef}
        data-testid="annotation-layer-data-grid"
        rows={gridData?.data.map(gridDataTableRow) ?? []}
        hideFooter={isNoData}
        className={`annotation-grid ${isNoData ? 'fullHeight' : ''}`}
        columns={updatedColumns}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        noRowsOverlay={NoRowsOverlay}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        onPageChange={onPageChangeHandler}
        onSortModelChange={handleSortModelChange}
        showPerPageOptions={true}
        pageSizeOptions={[PAGE_SIZE]}
        pagination
        rowCount={gridData?.totalRecords ?? 0}
        primaryKey="id"
        columnHeaderHeight={66}
        loading={isAnnotationListFetching}
      />
      <ArrModal
        showModal={showDeleteModal}
        title={t('annotationLayer.deleteAnnotation.title')}
        description={t('annotationLayer.deleteAnnotation.description')}
        onClose={() => setShowDeleteModal(false)}
        autoFocusCloseButton={true}
        disableFocusTitle={true}
        dataTestId="annotation-listgrid-modal"
        buttons={[
          {
            id: 'cancel-button',
            label: t('common.cancel'),
            dataTestId: 'alm-cancel-button',
            onClick: () => setShowDeleteModal(false),
            variant: 'secondary',
            ariaDisabled: isPending ? 'true' : 'false',
          },
          {
            id: 'delete-button',
            label: t('common.delete'),
            dataTestId: 'alm-delete-button',
            onClick: confirmDelete,
            variant: 'danger',
            loading: isPending,
            disabled: isPending,
          },
        ]}
      />
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => setShowToast(false)}
          status={[toastType]}
        />
      )}
      <UpsertAnnotationWrapper
        annotationID={annotationID}
        securityGroupId={Number(selectedGroup?.id ?? 0)}
        showModal={showUpsertAnnotationDialog}
        onClose={setShowUpsertAnnotationDialog}
        onUpsertAnnotation={onUpsertAnnotationSuccess}
      />
    </>
  );
};

export default AnnotationListGrid;
